import { IonButton, IonIcon, useIonAlert } from '@ionic/react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { sendSharp, shareSocialOutline } from 'ionicons/icons';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useDeleteSale } from '../../../api/hooks/sales/useDeleteSale';
import { useMutatePublicShare } from '../../../api/hooks/sales/useMutatePublicShare';
import { useSale } from '../../../api/hooks/sales/useSale';
import { useSendEmailToCustomer } from '../../../api/hooks/sales/useSendEmail';
import { usePostSale } from '../../../api/hooks/sales/useUpdateCreateSale';
import { useCompanyLogo } from '../../../api/hooks/useCompanyLogo';
import { useAbortSale } from '../../../api/hooks/viva/useAbortSale';
import {
  isProviderSaleFailure,
  isProviderSalePending,
  isProviderSaleStatusPending,
  useSaleState,
} from '../../../api/hooks/viva/useSaleState';
import { EmailModal, InvoicePdf, pdfStyles } from '../../../components';
import { Loading } from '../../../components/atoms/Loading';
import { ProviderTransitObligationBanner } from '../../../components/atoms/ProviderTransitObligationBanner';
import { DocView } from '../../../components/molecules/docView/DocView';
import { ENV } from '../../../config/env';
import { isAfter24Hours, nowDateInUtc } from '../../../config/utils/dateHelpers';
import { STANDALONE_FEATURE } from '../../../interfaces/enumerables';
import { PATHS } from '../../../navigation/data';
import {
  isDocPostableUtil,
  isDocPostedToMyData,
  isDocTypeStandardDeliveryNote,
} from '../../../services/utils/myDataUtils';
import { useHasActiveFeature } from '../../../services/utils/useHasActiveFeature';
import { useHasActivePlan } from '../../../services/utils/useHasActivePlan';
import { selectIsCompanyOwner, selectSelectedCompany } from '../../../store/companies/companiesSlice';
import { setToast } from '../../../store/core/coreSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { SaleContextInterface, SaleScreenContextProvider } from './SaleScreenContext';

interface Props {
  // Overrides the `:id` route param - set when rendered outside its own `/sales/view/:id` route
  // (see `SaleScreen`).
  id?: string;
}

export const SaleWrapper = ({ id: idProp }: Props = {}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const id = idProp ?? routeId;
  const { t } = useTranslation();
  const { data, isLoading, refetch, isRefetching } = useSale(id);
  const hasActivePlan = useHasActivePlan();
  const { enabled: isTransitEnabled } = useHasActiveFeature(STANDALONE_FEATURE.TRANSIT);
  const [presentAlert] = useIonAlert();
  const selectedCompany = useAppSelector(selectSelectedCompany);
  const isCompanyOwner = useAppSelector(selectIsCompanyOwner);
  const { doc, setDoc } = useContext<SaleContextInterface>(SaleScreenContextProvider);
  const { mutate: publicShareDoc } = useMutatePublicShare();
  const { isLoading: isLoadingMutation, mutate, isSuccess } = usePostSale(data);
  const { isLoading: isLoadingSendEmail, mutate: sendEmail } = useSendEmailToCustomer();
  const { data: logoData } = useCompanyLogo(data?.company?.logoUrl || selectedCompany?.logoUrl || '');
  const { mutate: deleteEntity, isLoading: isLoadingDelete } = useDeleteSale(navigate);
  const { mutate: abortSale, isLoading: isLoadingAbort } = useAbortSale();
  // `isDocTypeStandardDeliveryNote()` (9.1/9.2/9.3/9.33), not the broader `isDocTypeDeliveryNote()`
  // (which also includes 10.1/10.2, Quantity Receipt Form) - those never actually get a transit
  // lifecycle from myDATA (they only ever reach "Registered" status), so the "Register Delivery"
  // button below shouldn't be offered for them at all.
  const validForDelivery = useMemo(
    () => data?.mark && (isDocTypeStandardDeliveryNote(data?.documentType?.aadeCode) || data?.isDeliveryNote),
    [data?.documentType?.aadeCode, data?.isDeliveryNote, data?.mark],
  );

  const isDocPostable = useMemo(() => isDocPostableUtil(data?.documentType?.aadeCode), [data?.documentType?.aadeCode]);
  const providerPostingFailed = useMemo(() => isProviderSaleFailure(id, data), [id, data]);

  const { data: providerSaleState, isLoading: isLoadingSaleState } = useSaleState(id, data);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const openSendEmail = useCallback(() => {
    setShowEmailModal(true);
  }, []);
  const handleCancel = useCallback(() => {
    setShowEmailModal(false);
  }, []);

  const canProceedToAbort = useMemo(() => {
    if (
      !isLoading &&
      !isLoadingSaleState &&
      !isLoadingAbort &&
      isProviderSaleStatusPending(providerSaleState) &&
      !data?.failureReason
    ) {
      return isProviderSalePending(id, data);
    }

    return false;
  }, [data, id, isLoading, isLoadingAbort, isLoadingSaleState, providerSaleState]);

  useEffect(() => {
    if (isSuccess) {
      refetch();
    }
  }, [isSuccess, refetch]);

  useEffect(() => {
    if (data) {
      setDoc?.(data);
    }

    return () => {
      setDoc?.(null);
    };
  }, [data, setDoc]);

  const getLoading = useCallback(() => {
    if (isLoading || isLoadingDelete) {
      return <Loading backgroundFull />;
    }

    return null;
  }, [isLoading, isLoadingDelete]);

  const getLoadingPostDoc = useCallback(() => {
    if (isLoadingMutation || isLoadingSendEmail || isRefetching || isLoadingAbort) {
      return <Loading />;
    }

    return null;
  }, [isLoadingMutation, isLoadingSendEmail, isRefetching, isLoadingAbort]);

  const editDoc = useCallback(() => {
    navigate(`${PATHS.SALES}/edit/${id}`);
  }, [navigate, id]);

  const deleteConfirmation = useCallback(() => {
    presentAlert({
      mode: 'ios',
      header: t('FORMS.CONFIRM_DELETE_MSG'),
      buttons: [
        {
          text: t('SHARED.CANCEL'),
          role: 'cancel',
          cssClass: 'noTextTransformation',
        },
        {
          text: t('SHARED.CONFIRM'),
          role: 'confirm',
          handler: () => {
            // @ts-expect-error mutation ts error
            deleteEntity({ id });
          },
          cssClass: 'noTextTransformation',
        },
      ],
    });
  }, [presentAlert, t, deleteEntity, id]);

  const postDoc = useCallback(() => {
    if (data?.id) {
      // @ts-expect-error mutation ts error
      mutate({ entityId: data?.id });
    }
  }, [data?.id, mutate]);

  const abortSessionModal = useCallback(() => {
    if (data?.id) {
      presentAlert({
        mode: 'ios',
        header: t('FORMS.PROCEED_TO_POS_PAYMENT'),
        buttons: [
          {
            text: t('SHARED.CANCEL'),
            role: 'cancel',
            cssClass: 'noTextTransformation',
          },
          {
            text: t('SHARED.CONFIRM'),
            role: 'confirm',
            handler: () => abortSale({ id: data.id }),
            cssClass: 'noTextTransformation',
          },
        ],
      });
    }
  }, [data?.id, presentAlert, t, abortSale]);

  const postConfirmation = useCallback(() => {
    let msg = t('FORMS.CONFIRM_POST_MSG');
    const nowDate = nowDateInUtc();
    if (doc?.documentDate !== nowDate) msg = `${t('FORMS.CONFIRM_POST_MSG_DIFF_DATE')} ${msg}`;

    presentAlert({
      mode: 'ios',
      header: msg,
      buttons: [
        {
          text: t('SHARED.CANCEL'),
          role: 'cancel',
          cssClass: 'noTextTransformation',
        },
        {
          text: t('SHARED.CONFIRM'),
          role: 'confirm',
          handler: () => postDoc(),
          cssClass: 'noTextTransformation',
        },
      ],
    });
  }, [t, doc?.documentDate, presentAlert, postDoc]);

  const showInfoOnDownload = useCallback(() => {
    if (!isDocPostedToMyData(data) && isDocPostable) {
      dispatch(setToast({ value: { show: true, msg: t('FORMS.DOWNLOAD_INFO'), isError: false, duration: 10000 } }));
    }
  }, [data, dispatch, isDocPostable, t]);

  const downloadDoc = useCallback(() => {
    if (data?.id && data?.company && doc) {
      const fileName = `billease-${data?.customer?.companyName}-${data.documentDate}.pdf`;

      return (
        <PDFDownloadLink
          document={<InvoicePdf doc={data} logo={logoData} />}
          fileName={fileName}
          onClick={showInfoOnDownload}
          style={pdfStyles.ionButton}
        >
          <div className="flex-center-center-row-nowrap gap-8">
            {t('FORMS.DOWNLOAD_PDF')}
            <IonIcon style={{ fontSize: 20 }} slot="end" src="/assets/img/download.svg" />
          </div>
        </PDFDownloadLink>
      );
    }
  }, [data, doc, logoData, showInfoOnDownload, t]);

  const getDeliveryButton = useCallback(() => {
    if (validForDelivery && isTransitEnabled) {
      const goToDelivery = () => navigate(`${PATHS.DELIVERIES}/${data?.mark}`);
      return (
        <IonButton mode="md" onClick={goToDelivery}>
          {t('FORMS.DELIVERY_REGISTER_TRANSFER')}
          <IonIcon style={{ fontSize: 20 }} slot="end" src="/assets/img/deliveries.svg" />
        </IonButton>
      );
    }
  }, [data?.mark, isTransitEnabled, navigate, t, validForDelivery]);

  const getDeleteButton = useCallback(() => {
    if (isCompanyOwner && hasActivePlan && !data?.sentToAadeAt) {
      return (
        <IonButton mode="md" onClick={deleteConfirmation} fill="outline" className="ion-delete-button">
          {t('FORMS.DELETE')}
        </IonButton>
      );
    }
  }, [data?.sentToAadeAt, deleteConfirmation, hasActivePlan, isCompanyOwner, t]);

  const shareDocPublicly = useCallback(() => {
    if (id) {
      publicShareDoc({ entityId: id });

      const publicUrl = `${ENV.DOMAIN}share/doc/${id}`;
      navigator?.clipboard?.writeText(publicUrl);
      dispatch(
        setToast({ value: { show: true, msg: t('SHARED.COPIED_TO_CLIPBOARD'), isError: false, position: 'top' } }),
      );
    }
  }, [dispatch, id, publicShareDoc, t]);

  const getShareDocButton = useCallback(
    () => (
      <IonButton mode="md" onClick={shareDocPublicly}>
        {t('SHARED.SHARE')}
        <IonIcon slot="end" icon={shareSocialOutline}></IonIcon>
      </IonButton>
    ),
    [t, shareDocPublicly],
  );

  const sendEmailToCustomer = useCallback(
    (email: string) => {
      if (id && !providerPostingFailed) {
        sendEmail({ entityId: id, email });
      }
    },
    [id, providerPostingFailed, sendEmail],
  );

  const canSendEmail = useCallback(
    (emailSentAt: string | null | undefined) => {
      if (providerPostingFailed) return false;
      if (!emailSentAt) return true;

      return isAfter24Hours(emailSentAt);
    },
    [providerPostingFailed],
  );

  const getSendEmailButton = useCallback(() => {
    if (hasActivePlan && canSendEmail(data?.emailSentToCustomerAt)) {
      return (
        <IonButton mode="md" onClick={openSendEmail}>
          {t('SHARED.SEND_EMAIL')}
          <IonIcon slot="end" icon={sendSharp}></IonIcon>
        </IonButton>
      );
    }
  }, [hasActivePlan, data?.emailSentToCustomerAt, canSendEmail, openSendEmail, t]);

  const getPostButton = useCallback(() => {
    if (hasActivePlan && isDocPostable && !data?.sentToAadeAt) {
      return (
        <IonButton mode="md" onClick={postConfirmation}>
          {t('FORMS.POST_TO_MY_DATA')}
        </IonButton>
      );
    }
  }, [data?.sentToAadeAt, hasActivePlan, isDocPostable, postConfirmation, t]);

  const getAbortSessionButton = useCallback(() => {
    if (hasActivePlan && canProceedToAbort) {
      return (
        <IonButton mode="md" onClick={abortSessionModal} fill="outline" className="ion-delete-button">
          {t('SALE.CANCEL_PROVIDER_SALE')}
        </IonButton>
      );
    }
  }, [hasActivePlan, canProceedToAbort, abortSessionModal, t]);

  const getEditButton = useCallback(() => {
    if (hasActivePlan && !data?.sentToAadeAt) {
      return (
        <IonButton mode="md" onClick={editDoc}>
          {t('FORMS.EDIT')}
        </IonButton>
      );
    }
  }, [data?.sentToAadeAt, editDoc, hasActivePlan, t]);

  const getActionButtons = useCallback(() => {
    return (
      <div className="padding-b-10 flex-space-between-start-row-wrap width-100 p-t-16">
        <div className="flex-start-center-row-wrap">
          {getEditButton()}
          {getPostButton()}
          {getDeliveryButton()}
          {downloadDoc()}
          {getShareDocButton()}
          {getAbortSessionButton()}
        </div>
        <div className="flex-start-center-row-wrap">
          <div className="padding-r-10">{getSendEmailButton()}</div>
          {getDeleteButton()}
        </div>
      </div>
    );
  }, [
    getEditButton,
    getPostButton,
    getDeliveryButton,
    downloadDoc,
    getShareDocButton,
    getAbortSessionButton,
    getSendEmailButton,
    getDeleteButton,
  ]);

  // const getPdf = useCallback(() => {
  //   if (data?.id && selectedCompany && doc) {
  //     return (
  //       <PDFViewer style={{ width: '100%', height: '1300px' }} showToolbar={true}>
  //         <InvoicePdf doc={data} logo={logoData} />
  //       </PDFViewer>
  //     );
  //   }
  // }, [data, selectedCompany, logoData, doc]);

  const getDocView = useCallback(() => {
    if (data?.id && data?.company) {
      return <DocView doc={data} logo={logoData} />;
    }
  }, [data, logoData]);

  // const getSseComponent = useCallback(() => {
  //   if (id) return <SseComponent invoiceId={id} />;
  // }, [id]);

  return (
    <>
      {/* {getSseComponent()} */}
      {getLoading()}
      {getLoadingPostDoc()}
      <div className="width-100 padding-b-16 flex-start-center-column-nowrap padding-r-24 padding-l-24">
        <ProviderTransitObligationBanner />
        {getActionButtons()}
        <div className="scrollable-doc">{getDocView()}</div>
        <EmailModal
          visible={showEmailModal}
          initialEmail={data?.customerEmail || ''}
          onCancel={handleCancel}
          onSubmit={sendEmailToCustomer}
        />
        {/*{getPdf()}*/}
      </div>
    </>
  );
};
